import React from "react";
import { QuickStartTask } from "common/constants";
import type { PopoverOrigin } from "@mui/material/Popover";
import { getStaticUrl } from "js/config";

export interface PopoverConfig {
    position: (boundingBox: { top: number; left: number; width: number; height: number }) => { top: number; left: number };
    transformOrigin: PopoverOrigin;
}

// Base fields shared by all task types
interface BaseTaskConfig {
    title: string;
}

// Video-based task: shows a video (preview on hover or full screen)
export interface VideoTaskConfig extends BaseTaskConfig {
    type: "video";
    subtitle?: string;
    videoUrl: string;
}

// Popover-based task: shows a popover with content
export interface PopoverTaskConfig extends BaseTaskConfig {
    type: "popover";
    popoverTitle: string;
    popoverBody: (onCallback?: () => void) => React.ReactNode | string;
    popoverConfig?: PopoverConfig;
    highlightSelector?: string | string[];
    img: string;
}

// Union type: a task is either video-based OR popover-based
export type TaskConfig = VideoTaskConfig | PopoverTaskConfig;

// Video task configurations
export const VIDEO_TASK_CONFIGS: Record<string, VideoTaskConfig> = {
    [QuickStartTask.DISCOVER_SMART_SLIDES]: {
        type: "video",
        title: "Discover the editor",
        subtitle: "Learn how Smart Slides transform the way you build presentations.",
        videoUrl: getStaticUrl("/video/EditorOnboard.mp4")
    }
};

// Popover task configurations
export const POPOVER_TASK_CONFIGS: Record<string, PopoverTaskConfig> = {
    [QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE]: {
        type: "popover",
        title: "Add your first Smart Slide",
        highlightSelector: "#add-slide-button",
        img: getStaticUrl("/images/editor-onboarding/add-your-first-smart-slide.gif"),
        popoverTitle: "Welcome to the Smart Slide library",
        popoverBody: onCallback => (
            <span>Click on any slide to add it to your presentation. Not sure where to start? Try the <a
                href="#"
                style={{ textTransform: "capitalize" }}
                onClick={e => {
                    e.preventDefault();
                    onCallback?.();
                }}
            >compare radial bars</a> slide—it's one of our favorites.</span>
        ),
    },
    "add_your_first_smart_slide_end": {
        type: "popover",
        title: "Add your first Smart Slide",
        img: getStaticUrl("/images/editor-onboarding/add-your-first-smart-slide-end.gif"),
        popoverTitle: "There are endless ways to customize a Smart Slide",
        popoverBody: () => (
            <span>The buttons below each slide let you add content. You can replace placeholders with your own text, images, icons, logos, videos, and more.</span>
        ),
    },
    [QuickStartTask.DIVE_DEEPER_WITH_THE_PANEL]: {
        type: "popover",
        title: "Dive deeper with the panel",
        //highlightSelector: ["#side-menu-bar", "#right-panel-container"],
        highlightSelector: [".activeSidePanel", "#side-menu-bar"],
        popoverConfig: {
            position: box => ({ top: box.top, left: box.left }),
            transformOrigin: { vertical: "top", horizontal: "right" }
        },
        img: getStaticUrl("/images/editor-onboarding/dive-deeper-with-the-panel.gif"),
        popoverTitle: "For everything else, there's the panel",
        popoverBody: () => (
            <span>Look right 👉 to see all your slide options. Change colors, tweak your layout, see edit options, or browse slide ideas to switch your Smart Slide completely. </span>
        ),
    },
    [QuickStartTask.PERSONALIZE_YOUR_THEME]: {
        type: "popover",
        title: "Personalize your theme",
        highlightSelector: "#theme-menu",
        popoverConfig: {
            position: box => ({ top: box.top + box.height / 2, left: box.left + box.width }),
            transformOrigin: { vertical: "center", horizontal: "left" }
        },
        img: getStaticUrl("/images/editor-onboarding/personalize-your-theme.gif"),
        popoverTitle: "Personalize it with a theme",
        popoverBody: () => (
            <span>Pick a built-in theme or craft your own. Refine colors, fonts, image styles, and every detail in between.</span>
        ),
    },
    [QuickStartTask.PRESENT_LIKE_A_PRO]: {
        type: "popover",
        title: "Present and share",
        highlightSelector: ["#present-presentation", "#share-presentation"],
        popoverConfig: {
            position: box => ({ top: box.top + box.height, left: box.left }),
            transformOrigin: { vertical: "bottom", horizontal: "right" }
        },
        img: getStaticUrl("/images/editor-onboarding/present-like-a-pro.gif"),
        popoverTitle: "Show it off",
        popoverBody: () => (
            <span>When you <b>Present</b>, Smart Slides animate automatically in the Player. Use <b>Share</b> to collaborate or create a public link.</span>
        ),
    }
};

// Combined task configurations exported as a single object
export const TASK_CONFIGS: Record<string, TaskConfig> = {
    ...VIDEO_TASK_CONFIGS,
    ...POPOVER_TASK_CONFIGS
};
